import { useCallback, useState } from "react";

import { DeleteIcon } from "@chakra-ui/icons";
import { Button, Checkbox, HStack, Text, UseDisclosureReturn, VStack } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import { EnvironmentTypeEnum } from "@/__generated__/graphql";
import DeleteModal from "@/components/shared/DeleteModal";
import IconModal from "@/components/shared/IconModal";
import { useEnvironmentTeardownMutation } from "@/hooks/useEnvironmentPipelineMutation";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import legacyErrorHandler from "@/utils/legacyErrorHandler";

const DeleteEnvironment_Environment = graphql(`
  fragment DeleteEnvironment_Environment on Environment {
    id
    databaseId
    environmentType
    name
    production
    deletionProtected
  }
`);

function DeleteEnvironment({
  environment: propEnvironment,
  ...disclosureProps
}: UseDisclosureReturn & {
  environment?: FragmentType<typeof DeleteEnvironment_Environment>;
}) {
  const environment = getFragmentData(DeleteEnvironment_Environment, propEnvironment);
  const { applicationId, workspaceId } = useReadySearchParams();

  const [continueOnError, setContinueOnError] = useState<boolean>(false);

  const { onClose } = disclosureProps;

  const [teardownEnvironmentMutation] = useEnvironmentTeardownMutation({
    applicationId,
    continueOnError,
    environmentId: environment?.databaseId ?? "",
    workspaceId,
  });

  const deleteEnvironment = useCallback(() => {
    try {
      teardownEnvironmentMutation();
      onClose();
    } catch (error) {
      legacyErrorHandler(error);
    }
  }, [onClose, teardownEnvironmentMutation]);

  const modalBody = (
    <VStack gap={10} p={4}>
      <Text>
        {environment?.environmentType === EnvironmentTypeEnum.Permanent ? (
          <>
            This will <strong>PERMANENTLY</strong> delete your environment{" "}
            <strong>{environment?.name}</strong>
          </>
        ) : (
          "This environment will be permanently deleted along with all of its associated data"
        )}
      </Text>
      <HStack>
        <Checkbox onChange={e => setContinueOnError(!!e.target.checked)} />
        <Text>Force delete environment and ignore errors</Text>
      </HStack>
    </VStack>
  );

  if (!environment) return;

  if (environment?.deletionProtected) {
    return (
      <IconModal
        body="This environment has delete protection enabled. Please disable delete protection in the environment settings before deleting."
        footer={
          <Button as="a" mr={3} variant="ghost" onClick={() => disclosureProps.onClose()}>
            Cancel
          </Button>
        }
        header={`Delete ${environment?.name}`}
        icon={DeleteIcon}
        {...disclosureProps}
      />
    );
  }

  return (
    <DeleteModal
      additionalBody={modalBody}
      confirmationText={environment.name ?? undefined}
      confirmWithInput={environment.environmentType === EnvironmentTypeEnum.Permanent}
      deleteCallback={deleteEnvironment}
      resource={environment}
      {...disclosureProps}
    />
  );
}

export default DeleteEnvironment;
